import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Textarea } from '@hydrodigital/design-system';

const ALERT_VARIABLES = [
	{ key: '$LocationName', description: 'Location name' },
	{ key: '$AssetName', description: 'Asset name' },
	{ key: '$MainParameter', description: 'Main parameter name' },
	{ key: '$MainValue', description: 'Main parameter recommended value' },
	{ key: '$MainUnits', description: 'Main parameter recommended units' },
	{ key: '$MainThresholdHi', description: 'Main parameter high threshold' },
	{ key: '$MainThresholdLo', description: 'Main parameter low threshold' },
	{ key: '$AuxValueN', description: 'Aux param recommended value (replace N with param number)' },
	{ key: '$AuxUnitsN', description: 'Aux param unit (replace N with param number)' },
	{ key: '$AuxThresholdNHi', description: 'Aux param high threshold (replace N with param number)' },
	{ key: '$AuxThresholdNLo', description: 'Aux param low threshold (replace N with param number)' },
	{ key: '$TimeofAlert', description: 'Timestamp of alert or forecasted time' },
] as const;

interface AlertMessageTextareaProps {
	value: string;
	onChange: (value: string) => void;
	label: string;
	placeholder?: string;
	errorMessage?: string;
	maxLength?: number;
	disabled?: boolean;
}

export default function AlertMessageTextarea({
	value,
	onChange,
	label,
	placeholder = 'Type $ to insert a variable...',
	errorMessage,
	maxLength = 1000,
	disabled = false,
}: AlertMessageTextareaProps) {
	const [showDropdown, setShowDropdown] = useState(false);
	const [dropdownFilter, setDropdownFilter] = useState('');
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [dollarStartIndex, setDollarStartIndex] = useState<number | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const filteredVariables = ALERT_VARIABLES.filter((v) =>
		v.key.toLowerCase().includes(('$' + dropdownFilter).toLowerCase()),
	);

	// Close dropdown on outside click
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node) &&
				textareaRef.current &&
				!textareaRef.current.contains(e.target as Node)
			) {
				setShowDropdown(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Reset selected index when filter changes
	useEffect(() => {
		setSelectedIndex(0);
	}, [dropdownFilter]);

	const updateDropdownPosition = useCallback(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;

		// Create a mirror div to measure caret position
		const mirror = document.createElement('div');
		const computed = window.getComputedStyle(textarea);
		const properties = [
			'fontFamily',
			'fontSize',
			'fontWeight',
			'letterSpacing',
			'lineHeight',
			'padding',
			'paddingTop',
			'paddingRight',
			'paddingBottom',
			'paddingLeft',
			'border',
			'borderWidth',
			'boxSizing',
			'whiteSpace',
			'wordWrap',
			'wordBreak',
			'overflowWrap',
		] as const;

		mirror.style.position = 'absolute';
		mirror.style.visibility = 'hidden';
		mirror.style.width = computed.width;
		properties.forEach((prop) => {
			mirror.style[prop as any] = computed[prop as any];
		});
		mirror.style.whiteSpace = 'pre-wrap';
		mirror.style.wordWrap = 'break-word';
		mirror.style.overflow = 'hidden';

		const textBeforeCaret = textarea.value.substring(0, textarea.selectionStart);
		mirror.textContent = textBeforeCaret;

		const marker = document.createElement('span');
		marker.textContent = '|';
		mirror.appendChild(marker);

		document.body.appendChild(mirror);

		const textareaRect = textarea.getBoundingClientRect();
		const markerRect = marker.getBoundingClientRect();
		const mirrorRect = mirror.getBoundingClientRect();

		const top = markerRect.top - mirrorRect.top + textarea.offsetTop - textarea.scrollTop + 24;
		const left = markerRect.left - mirrorRect.left + textarea.offsetLeft - textarea.scrollLeft;

		document.body.removeChild(mirror);

		setDropdownPosition({ top, left: Math.min(left, textarea.offsetWidth - 420) });
	}, []);

	const insertVariable = useCallback(
		(variable: string) => {
			const textarea = textareaRef.current;
			if (!textarea || dollarStartIndex === null) return;

			const before = value.substring(0, dollarStartIndex);
			const after = value.substring(textarea.selectionStart);
			const newValue = before + variable + after;

			if (newValue.length <= maxLength) {
				onChange(newValue);

				// Restore cursor position after React re-render
				const cursorPos = dollarStartIndex + variable.length;
				requestAnimationFrame(() => {
					textarea.focus();
					textarea.setSelectionRange(cursorPos, cursorPos);
				});
			}

			setShowDropdown(false);
			setDropdownFilter('');
			setDollarStartIndex(null);
		},
		[value, onChange, dollarStartIndex, maxLength],
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const newValue = e.target.value;
			if (newValue.length > maxLength) return;

			onChange(newValue);

			const cursorPos = e.target.selectionStart;
			const textBeforeCursor = newValue.substring(0, cursorPos);

			// Find the last $ before cursor that isn't part of a completed variable
			const lastDollarIndex = textBeforeCursor.lastIndexOf('$');

			if (lastDollarIndex >= 0) {
				const textAfterDollar = textBeforeCursor.substring(lastDollarIndex + 1);
				// Only show dropdown if there's no space after $ (user is typing a variable name)
				if (!/\s/.test(textAfterDollar) && textAfterDollar.length <= 20) {
					setDollarStartIndex(lastDollarIndex);
					setDropdownFilter(textAfterDollar);
					setShowDropdown(true);
					requestAnimationFrame(updateDropdownPosition);
					return;
				}
			}

			setShowDropdown(false);
			setDropdownFilter('');
			setDollarStartIndex(null);
		},
		[onChange, maxLength, updateDropdownPosition],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (!showDropdown || filteredVariables.length === 0) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => (prev + 1) % filteredVariables.length);
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex(
						(prev) => (prev - 1 + filteredVariables.length) % filteredVariables.length,
					);
					break;
				case 'Enter':
				case 'Tab':
					e.preventDefault();
					insertVariable(filteredVariables[selectedIndex].key);
					break;
				case 'Escape':
					e.preventDefault();
					setShowDropdown(false);
					break;
			}
		},
		[showDropdown, filteredVariables, selectedIndex, insertVariable],
	);

	return (
		<div className="relative">
			<Textarea
				ref={textareaRef}
				label={label}
				showLabel={true}
				value={value}
				onChange={handleChange}
				size='small'
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				errorMessage={errorMessage}
				disabled={disabled}
				maxLength={maxLength}
				minRows={10}
				resize="none"
				helperText={`${value.length}/${maxLength} characters. Type $ to insert variables.`}
			/>

			{showDropdown && filteredVariables.length > 0 && (
				<div
					ref={dropdownRef}
					className="absolute z-50 w-[420px] max-h-[220px] overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg"
					style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
				>
					<div className="px-3 py-1.5 text-xs text-gray-400 border-b border-gray-100 font-medium">
						Variables — press Enter to insert
					</div>
					{filteredVariables.map((variable, index) => (
						<div
							key={variable.key}
							className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
								index === selectedIndex ? 'bg-blue-50' : 'bg-transparent'
							} ${index < filteredVariables.length - 1 ? 'border-b border-gray-50' : ''}`}
							onMouseEnter={() => setSelectedIndex(index)}
							onMouseDown={(e) => {
								e.preventDefault();
								insertVariable(variable.key);
							}}
						>
							<span className="font-mono text-sm text-blue-500 font-medium">
								{variable.key}
							</span>
							<span className="text-xs text-gray-400 ml-2">
								{variable.description}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
